// src/msal/boot.js
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "@/msal/authConfig";

let instance;

const isTestingEnv = process.env.VUE_APP_PLAYWRIGHT_TESTING === "true";

if (isTestingEnv) {
  console.log(
    "Playwright testing environment detected. Instantiating real and mock MSAL instances, to be provided based on the presence of the 'PLAYWRIGHT_MOCK_MSAL' local storage value.",
  );
  class MockPublicClientApplication {
    constructor() {
      this.callbacks = [];
      this.loggedOut = false;
      this.now = Math.floor(Date.now() / 1000); // current time in seconds
      // active account
      this.account = {
        homeAccountId: "mock-homeaccount-id",
        environment: "precisionsportstech.b2clogin.com",
        tenantId: "B2C_1_Dashboard_Users",
        username: "",
        localAccountId: "mock-localaccount-id",
        authorityType: "MSSTS",
        tenantProfiles: {},
        idTokenClaims: {
          exp: this.now + 3600, // expires in 1 hour
          nbf: this.now, // not before now
          ver: "1.0",
          iss: "https://precisionsportstech.b2clogin.com/mock-account-id/v2.0/",
          sub: "mock-localaccount-id",
          aud: "mock-audience-client-id",
          nonce: "mock-nonce-value",
          iat: this.now,
          auth_time: this.now - 10, // auth time 10 seconds ago
          oid: "mock-localaccount-id",
          given_name: "DefaultFirst",
          family_name: "DefaultLast",
          emails: ["default@email.address"],
          tfp: "B2C_1_Dashboard_Users",
          at_hash: "VpVc9htI27mYvq9SqPUpeg",
        },
      };
    }
    _updateAccountInfo() {
      // get the email and names from localStorage if available, otherwise use defaults
      // values stored in localStorage key "PLAYWRIGHT_MOCK_USER" in JSON format, e.g. {"email":"...","firstName":"...","lastName":"..."}
      const mockUserJson = localStorage?.getItem("PLAYWRIGHT_MOCK_USER");
      if (mockUserJson) {
        try {
          const mockUser = JSON.parse(mockUserJson);
          this.account.idTokenClaims.emails = [
            mockUser.email || "default@email.address",
          ];
          this.account.idTokenClaims.given_name =
            mockUser.firstName || "DefaultFirst";
          this.account.idTokenClaims.family_name =
            mockUser.lastName || "DefaultLast";
          // e.g. { newUser: true } to exercise AuthRedirect's sign-up logout
          Object.assign(this.account.idTokenClaims, mockUser.extraClaims);
          return;
        } catch (e) {
          console.error(
            "Failed to parse PLAYWRIGHT_MOCK_USER from localStorage:",
            e,
          );
        }
      }
      // No mock user in localStorage, use defaults
      this.account.idTokenClaims.emails = ["default@email.address"];
      this.account.idTokenClaims.given_name = "DefaultFirst";
      this.account.idTokenClaims.family_name = "DefaultLast";
    }
    async initialize() {
      this._updateAccountInfo();
      console.log(
        `MockPublicClientApplication initialized, with user: email: ${this.account.idTokenClaims.emails[0]}, firstName: ${this.account.idTokenClaims.given_name}, lastName: ${this.account.idTokenClaims.family_name}`,
      );
      return Promise.resolve();
    }
    addEventCallback(callback) {
      this.callbacks.push(callback);
      return "mock-callback-id";
    }
    removeEventCallback() {}
    _emitEvent(eventType) {
      this.callbacks.forEach((callback) => callback({ eventType }));
    }
    getActiveAccount() {
      return this.loggedOut ? null : this.account;
    }
    getAllAccounts() {
      return this.loggedOut ? [] : [this.getActiveAccount()];
    }
    getAccount(accountFilter) {
      if (this.loggedOut) return null;
      if (accountFilter?.homeAccountId === this.account.homeAccountId) {
        return this.account;
      }
    }
    async acquireTokenSilent() {
      return Promise.resolve({
        accessToken: `mock-token-config.${btoa(
          JSON.stringify({
            exp: this.account.idTokenClaims.exp,
          }),
        )}.mock-token`,
        idToken: "mock-id-token",
      });
    }
    async ssoSilent() {
      return this.acquireTokenSilent();
    }
    async acquireTokenRedirect() {
      return Promise.resolve();
    }
    async loginRedirect() {
      return Promise.resolve();
    }
    async logoutRedirect() {
      console.warn(
        "[MockMSAL] logoutRedirect called. MockMSAL should not be used for any tests that require actual logout behavior.",
      );
      // Like real MSAL: announce the logout and clear the account cache
      // straight away, but let the page keep running briefly before the
      // redirect lands, so in-flight work sees an empty cache.
      this._emitEvent("msal:logoutStart");
      this.loggedOut = true;
      // if window exists, redirect to "about:blank" to simulate a logout redirect, otherwise just return a rejected promise
      if (typeof window !== "undefined") {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        window.location.href = "about:blank";
        console.log(
          "[MockMSAL] Simulated logout redirect to about:blank, instead of actually logging out.",
        );
        return Promise.resolve();
      }
      return Promise.reject(
        new Error(
          "MockMSAL logoutRedirect called. MockMSAL should not be used for any tests that require actual logout behavior. No window object available to simulate redirect.",
        ),
      );
    }
    async handleRedirectPromise() {
      return Promise.resolve({
        account: this.getActiveAccount(),
      });
    }
    setActiveAccount(account) {
      this.account = account;
      this._updateAccountInfo();
    }
    setNavigationClient() {}
  }
  const mockMsalInstance = new MockPublicClientApplication();

  const realMsalInstance = new PublicClientApplication(msalConfig);

  instance = new Proxy(realMsalInstance, {
    get(target, prop) {
      // If the localStorage flag is set to "true", use the mock MSAL instance
      if (localStorage?.getItem("PLAYWRIGHT_MOCK_MSAL") === "true") {
        if (prop in mockMsalInstance) {
          if (typeof mockMsalInstance[prop] === "function") {
            return mockMsalInstance[prop].bind(mockMsalInstance);
          }
          return mockMsalInstance[prop];
        }
        // If the method is not found on mock, warn and return a no-op function
        console.warn(
          `[MSAL Proxy] Method '${prop}' not defined on mock MSAL instance, returning no-op function.`,
        );
        return () => {
          throw new Error(
            `[MSAL Proxy] No-op function called for method '${prop}' on mock MSAL instance. This method is not implemented in the mock, and must be implemented in src/msal/boot.js if it is needed for testing. Please implement the method in the MockPublicClientApplication class.`,
          );
        };
      } else {
        // If the localStorage flag is not set to "true", use the real MSAL instance
        return Reflect.get(target, prop);
      }
    },
  });
} else {
  // Normal production and standard local development loop
  console.log("Normal environment detected. Instantiating real MSAL instance.");

  instance = new PublicClientApplication(msalConfig);
}

// Create a PublicClientApplication instance with the MSAL configuration THIS SHOULD BE THE ONE AND ONLY INSTANCE
export const msalInstance = instance;

let _readyPromise = null;

/**
 * Ensure MSAL is initialized, redirect handled, account set, and cache seeded.
 * @returns {*|Promise<boolean>}
 */
export function waitForMsalReady() {
  if (_readyPromise) return _readyPromise;

  _readyPromise = (async () => {
    await msalInstance.initialize();

    const result = await msalInstance.handleRedirectPromise().catch((e) => {
      console.error("[MSAL] handleRedirectPromise failed:", e);
      return null;
    });
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    } else {
      const account =
        msalInstance.getActiveAccount?.() ||
        msalInstance.getAllAccounts()[0] ||
        null;

      if (account) msalInstance.setActiveAccount(account);
    }

    window.__pca = msalInstance;
    return true;
  })();

  return _readyPromise;
}
